import React, { useState, useEffect, useRef } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCircleInfo,
  faMapMarkerAlt,
  faUpload,
  faCircleXmark,
  faPhone,
} from '@fortawesome/free-solid-svg-icons'
import useAxiosPrivate from '../../../hooks/useAxiosPrivate'
import GPMapSelection from '../../../components/GPMapSelection'
import ClientAddressForm from '../../../components/ClientAddressForm'
import GpAddressForm from '../../../components/GpAddressForm'
import SupportingDocs from '../../../components/SupportingDocs'
import PreferredContactNumber from '../../../components/PreferredContactNumber'
import useAuth from '../../../hooks/useAuth'
import { toast } from 'sonner'

const StepThree = ({
  selectedAppointment,
  selectedGP,
  setSelectedGP,
  setStep,
  setContactDetails,
  onValidityChange,
  trigger,
  dependant
}) => {
  const [reason, setReason] = useState('')
  const [isNotRegisteredWithGP, setIsNotRegisteredWithGP] = useState(false)
  const [showGpMap, setShowGpMap] = useState(false)
  const [hasGP, setHasGP] = useState(false)
  const { auth } = useAuth()
  const [enterDetailsManually, setEnterDetailsManually] = useState(false)
  const [manualDetails, setManualDetails] = useState({
    name: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    postcode: '',
    country: '',
    phone: '',
    email: '',
  })
  const [clientAddressData, setClientAddressData] = useState([])
  const [fileList, setFileList] = useState([])
  const [errorMsg, setErrorMsg] = useState('')
  const axios = useAxiosPrivate()
  const [phoneNumber, setPhoneNumber] = useState(auth.user.phone_number || '')

  useEffect(() => {
    if (selectedAppointment) {
      holdAppointment(selectedAppointment.id)
      getBookings()
    }
    
    if (
      Object.keys(auth?.user?.user_info?.gp).length > 0
    ) {
      setHasGP(true)
      trigger(false)
    } else {
      setHasGP(false)
      trigger(true)
    }
  }, [])

  useEffect(() => {
    setContactDetails({
      ...clientAddressData,
      phoneNumber: phoneNumber,
    })

    selectedAppointment.phoneNumber = phoneNumber
    selectedAppointment.email = auth.user.email;
  }, [clientAddressData, phoneNumber])

  useEffect(() => {
    const valid = validate()
    if (typeof onValidityChange === 'function') {
      onValidityChange(valid)
    }
  }, [reason, clientAddressData, phoneNumber])


  const handleGpSelection = async (gpDetails) => {
    setSelectedGP(gpDetails)
  }

  const validate = () => {
    return reason !== '' && clientAddressData !== '' && phoneNumber !== '';
  }

  const holdAppointment = async (id) => {
    try {
      await axios.post('/appointments/hold', {
        id: id,
        user_id: auth?.user?.id,
      })
    } catch (error) {
      console.error('Error holding appointment:', error)
    }
  }

  const getBookings = async () => {
    try {
      await axios.get(`/bookings/user/${auth?.user?.id}/all`)
    } catch (error) {
      console.error('Error fetching bookings:', error)
    }
  }

  const handleClearSelectedGP = () => {
    setSelectedGP(null)
    setEnterDetailsManually(false)
    setManualDetails({
      name: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      postcode: '',
      country: '',
      phone: '',
      email: '',
    })
    setIsNotRegisteredWithGP(true)
    setShowGpMap(false)
  }

  const handleEditSelectedGP = () => {
    if (!selectedGP) return
    setEnterDetailsManually(true)
    const parts = selectedGP.address
      ? selectedGP.address.split(',').map((part) => part.trim())
      : []
    const line1 = parts[0] || ''
    const line2 = parts[1] || ''
    const town = parts[2] || ''

    setManualDetails({
      name: selectedGP.name || selectedGP.surgeryName || '',
      addressLine1: line1,
      addressLine2: line2,
      city: town,
      postcode: selectedGP.postcode || '',
      country: selectedGP.country || '',
      phone: selectedGP.phone || '',
      email: selectedGP.email || '',
    })
  }

  const updateReason = (e) => {
    setReason(e.target.value)
    selectedAppointment.reason = e.target.value
  }


  const CountdownTimer = ({ initialMinutes }) => {
    const [timeLeft, setTimeLeft] = useState(initialMinutes * 60)

    useEffect(() => {
      const timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timer)
            return 0
          }
          return prev - 1
        })
      }, 1000)

      return () => clearInterval(timer)
    }, [])

    const minutes = Math.floor(timeLeft / 60)
    const seconds = timeLeft % 60

    useEffect(() => {
      if (timeLeft === 0) {
        alert('Your session has expired. The page will now refresh.')
        window.location.reload()
      }
    }, [timeLeft])

    return (
      <span>
        {minutes >= 1
          ? `${minutes + (minutes === 1 ? ' minute' : ' minutes')}`
          : `${seconds} seconds`}
      </span>
    )
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8 text-portal-dark-blue">
      {showGpMap ? (
        <GPMapSelection
          onHideMap={() => setShowGpMap(false)}
          selectedGP={selectedGP}
          setSelectedGP={setSelectedGP}
          onChoose={async (gpDetails) => {
            handleGpSelection(gpDetails)
            setIsNotRegisteredWithGP(false)
            setEnterDetailsManually(false)
            setShowGpMap(false)
          }}
        />
      ) : (
        <>
          {/* Info Alert */}
          <div className="border border-blue-400 bg-blue-50 text-blue-800 p-4 rounded-lg flex items-start gap-3">
            <FontAwesomeIcon icon={faCircleInfo} className="mt-1 text-lg" />
            <p className="text-sm">
              Your appointment is now held for{' '}
              <CountdownTimer initialMinutes={4.5} />. Please confirm your
              booking within this time to avoid losing the selected timeslot.
            </p>
          </div>

          {/* Location */}
          <div>
            <h2 className="text-lg font-semibold mb-1 flex items-center gap-2">
              Your location during consultation{' '}
              <FontAwesomeIcon icon={faCircleInfo} />
            </h2>
            <p className="text-sm text-gray-600 mb-2">
              Please enter a UK postcode to look up your address. If you will be
              in a different country, please enter the address manually.
            </p>
            <ClientAddressForm
              clientAddressData={clientAddressData}
              setClientAddressData={setClientAddressData}
            />
          </div>

          {/* Phone number */}
          <PreferredContactNumber
            phoneNumber={phoneNumber}
            setPhoneNumber={setPhoneNumber}
          />

          {/* Reason for booking */}
          <div>
            <h2 className="text-lg font-semibold mb-1">Reason for booking</h2>
            <label
              htmlFor="reason"
              className="text-sm font-medium text-gray-600 mb-1 block"
            >
              Symptoms of concern
            </label>
            <textarea
              id="reason"
              rows={4}
              placeholder="Please briefly describe your reason for booking and symptoms of concern."
              value={reason}
              onChange={(e) => updateReason(e)}
              className="w-full border rounded-md p-3 text-sm focus:outline-none focus:ring focus:border-violet-300 focus:ring-violet-300"
            />
          </div>

          {/* Supporting documents */}
          <div>
            <SupportingDocs
              id={selectedAppointment?.id}
              isBooked={false}
              uploadUrl="/appointments/supportingDocs"
              deleteUrl="/appointments/supportingDocs/delete"
            />
          </div>

          {!hasGP && (
            <div>
              <h2 className="text-lg font-semibold mb-1">GP details</h2>
              <p className="text-sm text-gray-600 mb-3">
                Your GP details allow us to send consultation notes to your
                local surgery to keep your medical records up-to-date.
              </p>

              {enterDetailsManually ? (
                <>
                <GpAddressForm
                  manualDetails={manualDetails}
                  setManualDetails={setManualDetails}
                />
                <div className="mt-2">
                <label className="text-sm flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isNotRegisteredWithGP}
                    onChange={() => {
                      setIsNotRegisteredWithGP(!isNotRegisteredWithGP)
                      setEnterDetailsManually(false) // Hide manual details when toggling this
                      setShowGpMap(false)
                      setSelectedGP(null)
                    }}
                  />
                  I am not registered with a GP surgery
                </label>
              </div>
              </>
              ) : selectedGP && Object.keys(selectedGP).length > 0 ? (
                <div className="bg-white p-4 rounded-md shadow-md border border-gray-300 relative">
                  <h3 className="font-semibold text-portal-dark-blue mb-2">
                    {selectedGP.name || selectedGP.surgeryName || 'GP Surgery'}
                  </h3>
                  <div className="mb-4">
                    {selectedGP.address && <p>{selectedGP.address}</p>}
                    {selectedGP.postcode && <p>{selectedGP.postcode}</p>}
                  </div>
                  {selectedGP.phone && (
                    <p className="mt-4 font-medium flex items-center gap-2 text-sm text-gray-700">
                      <FontAwesomeIcon icon={faPhone} className="text-gray-500" />
                      {selectedGP.phone}
                    </p>
                  )}
                  <div className="absolute bottom-3 right-3 flex gap-4 text-sm text-blue-600 cursor-pointer select-none">
                    <span onClick={handleClearSelectedGP} className="hover:underline">
                      Clear
                    </span>
                    <span onClick={handleEditSelectedGP} className="hover:underline">
                      Edit
                    </span>
                  </div>
                </div>
              ) : (
                <>
                  <button
                    onClick={() => setShowGpMap(true)}
                    className="w-full max-w-[350px] py-3 rounded-md border border-portal-dark-blue flex justify-center items-center gap-4 text-portal-dark-blue hover:bg-violet-50 transition"
                  >
                    <FontAwesomeIcon icon={faMapMarkerAlt} />
                    Select your GP surgery from map
                  </button>
                  <button
                    onClick={() => setEnterDetailsManually(true)}
                    className="text-sm mt-4 text-blue-600 underline"
                  >
                    Enter GP details manually
                  </button>
                </>
              )}

            </div>
          )}
        </>
      )}
    </div>
  )
}

export default StepThree
